import React, { Fragment, useCallback, useMemo, useState } from "react";
import { icon } from "@fortawesome/fontawesome-svg-core/import.macro";

import AdministrationHeader from "../../components/header-components/AdministrationHeader.jsx";
import TableCard from "../../components/ui-components/TableCard";
import FlexTable from "../../components/ui-components/FlexTable";
import EmptyState from "../../components/ui-components/EmptyState";
import Badge from "../../components/ui-components/Badge";
import StatusPill from "../Budgets/components/StatusPill";
import { toast } from "../../components/ui-components/Toast";
import { useRoles } from "../roles/api";
import {
  useAuditEvents,
  useEffectiveAccess,
  downloadAuditCsv,
  downloadEffectiveAccessCsv,
  downloadJson,
} from "./api";

// fmtDate renders an ISO timestamp as a short local date-time. Returns "—" for empty input.
const fmtDate = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime()) ? "—" : d.toLocaleString();
};

// fmtMoney renders integer cents as a euro amount (approval-authority ceilings
// are stored in cents, matching the rest of the budgeting domain).
const fmtMoney = (cents) => {
  if (cents == null) return "—";
  return `€${(cents / 100).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;
};

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

// severityTone maps the domain's free-form severity string ("info"/"warning"/
// "critical", see backend_go internal/budgeting/domain/audit.go) onto one of
// Badge's fixed status tones -- "critical" isn't itself a recognized Badge
// status key, so it rides the "danger" tone with its own label text.
const severityTone = (severity) => (severity === "critical" ? "danger" : severity || "neutral");

const toDateInputValue = (d) => d.toISOString().slice(0, 10);
const today = new Date();
const DEFAULT_TO = toDateInputValue(today);
const DEFAULT_FROM = toDateInputValue(new Date(today.getTime() - 30 * 24 * 60 * 60 * 1000));

// AuditEvidencePage — the Settings-shell auditor page (Sub-project D3, Task
// 5): a date-ranged, filterable audit-event log and a point-in-time
// effective-access snapshot (who can do what, tenant-owner posture, SoD
// findings), each with CSV/JSON evidence export. The route itself is
// capability-gated on audit.export, so this page only ever renders for
// holders of that capability.
export default function AuditEvidencePage() {
  // --- Audit log state/filters ---
  const [fromDate, setFromDate] = useState(DEFAULT_FROM);
  const [toDate, setToDate] = useState(DEFAULT_TO);
  const [eventType, setEventType] = useState("");
  const [objectType, setObjectType] = useState("");
  const [severity, setSeverity] = useState("");

  const auditParams = useMemo(() => {
    const p = {};
    if (fromDate) p.from = new Date(`${fromDate}T00:00:00Z`).toISOString();
    if (toDate) p.to = new Date(`${toDate}T23:59:59Z`).toISOString();
    if (eventType.trim()) p.event_type = eventType.trim();
    if (objectType.trim()) p.object_type = objectType.trim();
    if (severity) p.severity = severity;
    return p;
  }, [fromDate, toDate, eventType, objectType, severity]);

  const auditQ = useAuditEvents(auditParams);
  const events = auditQ.data?.events ?? [];
  const truncated = !!auditQ.data?.truncated;

  const [exportingAuditCsv, setExportingAuditCsv] = useState(false);
  const handleAuditCsvExport = async () => {
    setExportingAuditCsv(true);
    try {
      await downloadAuditCsv(auditParams);
    } catch (err) {
      toast.error(err?.message || "Failed to export the audit log. Please try again.");
    } finally {
      setExportingAuditCsv(false);
    }
  };
  const handleAuditJsonExport = () => downloadJson({ events, truncated }, "audit-events.json");

  const auditColumns = useMemo(
    () => [
      {
        accessor: "eventTime",
        Header: () => <span>Event time</span>,
        Cell: ({ value }) => <span className="text-text-2">{fmtDate(value)}</span>,
        width: 175,
        minWidth: 150,
      },
      {
        accessor: "actorUserId",
        Header: () => <span>Actor</span>,
        Cell: ({ value }) => (
          <span className="text-text">{value != null ? `User #${value}` : "System"}</span>
        ),
        width: 110,
        minWidth: 100,
      },
      {
        accessor: "eventType",
        Header: () => <span>Event type</span>,
        Cell: ({ value }) => <span className="font-medium text-text">{value}</span>,
        flex: 1,
        minWidth: 140,
      },
      {
        id: "object",
        Header: () => <span>Object</span>,
        Cell: ({ row }) => (
          <span className="text-text-2">
            {row.original.objectType} <span className="text-text-3">#{row.original.objectId}</span>
          </span>
        ),
        flex: 1,
        minWidth: 160,
      },
      {
        accessor: "severity",
        Header: () => <span>Severity</span>,
        Cell: ({ value }) => <Badge label={cap(value)} status={severityTone(value)} size="sm" />,
        width: 110,
        minWidth: 100,
      },
      {
        accessor: "actorIp",
        Header: () => <span>IP</span>,
        Cell: ({ value }) => <span className="font-mono text-xs text-text-3">{value || "—"}</span>,
        width: 130,
        minWidth: 110,
      },
    ],
    []
  );

  // --- Effective access ---
  const accessQ = useEffectiveAccess();
  const rolesQ = useRoles();
  const report = accessQ.data;
  const users = report?.users ?? [];
  const serviceAccounts = report?.serviceAccounts ?? [];
  const tenant = report?.tenant;
  const sodFindings = report?.sodFindings ?? [];
  const roles = useMemo(() => rolesQ.data || [], [rolesQ.data]);

  const roleNameByCode = useMemo(() => {
    const m = new Map();
    for (const r of roles) m.set(r.code, r.name);
    return m;
  }, [roles]);

  const [exportingAccessCsv, setExportingAccessCsv] = useState(false);
  const handleAccessCsvExport = async () => {
    // Guard against firing before the report has loaded -- the CSV endpoint
    // fetches independently of `report`, but there's nothing meaningful to
    // export yet, and clicking mid-load shouldn't kick off a second
    // in-flight export.
    if (isAccessLoading) return;
    setExportingAccessCsv(true);
    try {
      await downloadEffectiveAccessCsv();
    } catch (err) {
      toast.error(err?.message || "Failed to export the effective-access snapshot. Please try again.");
    } finally {
      setExportingAccessCsv(false);
    }
  };
  // Guarded: `report` is undefined until useEffectiveAccess() resolves. Without
  // this check, clicking Export JSON before load calls
  // downloadJson(undefined, ...) -> JSON.stringify(undefined) -> a file whose
  // entire contents are the literal text "undefined" -- unacceptable for an
  // auditor evidence export. The button below is also disabled while loading
  // as a second, belt-and-suspenders guard.
  const handleAccessJsonExport = () => {
    if (isAccessLoading || !report) return;
    downloadJson(report, "effective-access.json");
  };

  const roleChips = useCallback((codes) => (
    <div className="flex flex-wrap gap-1.5">
      {(codes || []).length > 0 ? (
        (codes || []).map((code) => (
          <span key={code} className="rounded border border-border bg-surface-2 px-2 py-0.5 text-xs text-text">
            {roleNameByCode.get(code) || code}
          </span>
        ))
      ) : (
        <span className="text-text-3">—</span>
      )}
    </div>
  ), [roleNameByCode]);

  const userColumns = useMemo(
    () => [
      {
        accessor: "email",
        Header: () => <span>Email</span>,
        Cell: ({ value }) => <span className="font-medium text-text truncate">{value}</span>,
        flex: 1,
        minWidth: 200,
      },
      {
        accessor: "roleCodes",
        Header: () => <span>Roles</span>,
        Cell: ({ value }) => roleChips(value),
        flex: 1,
        minWidth: 200,
      },
      {
        accessor: "capabilities",
        Header: () => <span>Key capabilities</span>,
        Cell: ({ value }) => {
          const caps = value || [];
          const shown = caps.slice(0, 3);
          const rest = caps.length - shown.length;
          return (
            <div className="flex flex-wrap gap-1.5">
              {shown.length === 0 ? (
                <span className="text-text-3">—</span>
              ) : (
                shown.map((c) => (
                  <span key={c} className="rounded bg-primary-soft px-2 py-0.5 text-xs text-primary-hover">
                    {c}
                  </span>
                ))
              )}
              {rest > 0 && <span className="text-xs text-text-3">+{rest} more</span>}
            </div>
          );
        },
        flex: 1,
        minWidth: 220,
      },
      {
        accessor: "scopeGrants",
        Header: () => <span>Scope grants</span>,
        Cell: ({ value }) => {
          const grants = value || [];
          return grants.length === 0 ? (
            <span className="text-text-3">None</span>
          ) : (
            <span className="text-text-2" title={grants.map((g) => `${g.label} (${g.clauseCount} clauses)`).join(", ")}>
              {grants.length} grant{grants.length === 1 ? "" : "s"}
            </span>
          );
        },
        width: 140,
        minWidth: 120,
      },
      {
        accessor: "approvalAuthorities",
        Header: () => <span>Approval authority</span>,
        Cell: ({ value }) => {
          const auths = value || [];
          return auths.length === 0 ? (
            <span className="text-text-3">None</span>
          ) : (
            <div className="flex flex-col gap-0.5">
              {auths.map((a, i) => (
                <span key={i} className="text-xs text-text-2">
                  {a.objectType} · up to {fmtMoney(a.maxAmountCents)}
                  {!a.isEnabled && <span className="text-text-3"> (disabled)</span>}
                </span>
              ))}
            </div>
          );
        },
        flex: 1,
        minWidth: 200,
      },
      {
        accessor: "isTenantOwner",
        Header: () => <span>Tenant owner</span>,
        Cell: ({ value }) => (value ? <Badge label="Tenant owner" status="info" /> : <span className="text-text-3">—</span>),
        width: 130,
        minWidth: 120,
      },
    ],
    [roleChips]
  );

  const serviceAccountColumns = useMemo(
    () => [
      {
        accessor: "label",
        Header: () => <span>Label</span>,
        Cell: ({ value }) => <span className="font-medium text-text truncate">{value}</span>,
        flex: 1,
        minWidth: 160,
      },
      {
        accessor: "roleCodes",
        Header: () => <span>Roles</span>,
        Cell: ({ value }) => roleChips(value),
        flex: 1,
        minWidth: 200,
      },
      {
        accessor: "status",
        Header: () => <span>Status</span>,
        Cell: ({ value }) => <StatusPill status={value} />,
        width: 130,
        minWidth: 120,
      },
    ],
    [roleChips]
  );

  const accessReviewDue = tenant?.accessReviewStatus === "due";
  const isAccessLoading = accessQ.isLoading || rolesQ.isLoading;

  return (
    <Fragment>
      <AdministrationHeader title="Audit & evidence" showPeriodSelector={false} />
      <div className="w-full p-8 space-y-8">
        <p className="max-w-2xl text-sm text-text-2">
          Auditor evidence: a date-ranged log of every audit event recorded across the tenant, and
          a point-in-time snapshot of who can do what. Both export to CSV or JSON for handoff to an
          external auditor.
        </p>

        {/* --- Audit log --- */}
        <section className="space-y-3">
          <h2 className="text-sm font-semibold uppercase tracking-wide text-text-2">Audit log</h2>

          <div className="flex flex-wrap items-end gap-3 rounded-card border border-card-bd bg-card p-4">
            <div>
              <label htmlFor="audit-from" className="mb-1 block text-xs font-medium text-text-2">
                From
              </label>
              <input
                id="audit-from"
                type="date"
                value={fromDate}
                onChange={(e) => setFromDate(e.target.value)}
                className="box-border rounded-ctrl border border-solid border-border px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="audit-to" className="mb-1 block text-xs font-medium text-text-2">
                To
              </label>
              <input
                id="audit-to"
                type="date"
                value={toDate}
                onChange={(e) => setToDate(e.target.value)}
                className="box-border rounded-ctrl border border-solid border-border px-2.5 py-1.5 text-sm"
              />
            </div>
            <div>
              <label htmlFor="audit-event-type" className="mb-1 block text-xs font-medium text-text-2">
                Event type
              </label>
              <input
                id="audit-event-type"
                type="text"
                value={eventType}
                onChange={(e) => setEventType(e.target.value)}
                placeholder="e.g. created"
                className="box-border rounded-ctrl border border-solid border-border px-2.5 py-1.5 text-sm"
                style={{ width: 160 }}
              />
            </div>
            <div>
              <label htmlFor="audit-object-type" className="mb-1 block text-xs font-medium text-text-2">
                Object type
              </label>
              <input
                id="audit-object-type"
                type="text"
                value={objectType}
                onChange={(e) => setObjectType(e.target.value)}
                placeholder="e.g. budget"
                className="box-border rounded-ctrl border border-solid border-border px-2.5 py-1.5 text-sm"
                style={{ width: 160 }}
              />
            </div>
            <div>
              <label htmlFor="audit-severity" className="mb-1 block text-xs font-medium text-text-2">
                Severity
              </label>
              <select
                id="audit-severity"
                value={severity}
                onChange={(e) => setSeverity(e.target.value)}
                className="box-border rounded-ctrl border border-solid border-border px-2.5 py-1.5 text-sm"
              >
                <option value="">All</option>
                <option value="info">Info</option>
                <option value="warning">Warning</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div className="ml-auto flex items-center gap-2">
              <button
                type="button"
                onClick={handleAuditCsvExport}
                disabled={exportingAuditCsv}
                className="box-border rounded-ctrl border border-solid border-border bg-card px-3 py-1.5 text-sm font-medium text-text transition-colors hover:bg-surface-2 disabled:opacity-50"
              >
                {exportingAuditCsv ? "Exporting…" : "Export CSV"}
              </button>
              <button
                type="button"
                onClick={handleAuditJsonExport}
                className="box-border rounded-ctrl border border-solid border-border bg-card px-3 py-1.5 text-sm font-medium text-text transition-colors hover:bg-surface-2"
              >
                Export JSON
              </button>
            </div>
          </div>

          {truncated && (
            <div className="rounded-card border border-warning bg-warning-soft px-4 py-2 text-sm text-text">
              Showing the maximum number of events for this range — narrow the date range or filters
              to see the full set.
            </div>
          )}

          <TableCard
            subtitle={auditQ.isLoading ? "Loading…" : `Showing ${events.length} event${events.length === 1 ? "" : "s"}`}
            isLoading={auditQ.isLoading}
            error={auditQ.error}
            errorMessage="Error loading audit events"
            onRetry={() => auditQ.refetch()}
          >
            {events.length === 0 ? (
              <EmptyState
                icon={icon({ name: "list-check", style: "light" })}
                title="No audit events in this range"
                message="Adjust the date range or filters to widen the search."
              />
            ) : (
              <FlexTable columns={auditColumns} data={events} getRowId={(e) => e.id} />
            )}
          </TableCard>
        </section>

        {/* --- Effective access --- */}
        <section className="space-y-3">
          <div className="flex items-center justify-between gap-3">
            <h2 className="text-sm font-semibold uppercase tracking-wide text-text-2">Effective access</h2>
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={handleAccessCsvExport}
                disabled={exportingAccessCsv || isAccessLoading}
                className="box-border rounded-ctrl border border-solid border-border bg-card px-3 py-1.5 text-sm font-medium text-text transition-colors hover:bg-surface-2 disabled:opacity-50"
              >
                {exportingAccessCsv ? "Exporting…" : "Export CSV"}
              </button>
              <button
                type="button"
                onClick={handleAccessJsonExport}
                disabled={isAccessLoading || !report}
                className="box-border rounded-ctrl border border-solid border-border bg-card px-3 py-1.5 text-sm font-medium text-text transition-colors hover:bg-surface-2 disabled:opacity-50"
              >
                Export JSON
              </button>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
            <div className="rounded-card border border-card-bd bg-card p-4 lg:col-span-1">
              <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-3">Tenant summary</div>
              <div className="flex items-center gap-3 flex-wrap">
                {accessReviewDue ? (
                  <Badge label="Review due" status="warning" />
                ) : (
                  <Badge label="Up to date" status="success" />
                )}
                <span className="text-sm text-text-2">
                  {tenant?.tenantOwnerCount ?? 0} tenant owner{(tenant?.tenantOwnerCount ?? 0) === 1 ? "" : "s"}
                </span>
              </div>
              {tenant?.accessReviewLastReviewedAt && (
                <div className="mt-1 text-xs text-text-3">Last reviewed {fmtDate(tenant.accessReviewLastReviewedAt)}</div>
              )}

              <div className="mt-4 border-t border-card-bd pt-3">
                <div className="mb-2 text-xs font-semibold uppercase tracking-wide text-text-3">
                  Segregation-of-duties findings
                </div>
                {sodFindings.length === 0 ? (
                  <p className="text-sm text-text-3">No segregation-of-duties conflicts</p>
                ) : (
                  <ul className="space-y-2">
                    {sodFindings.map((f, i) => (
                      <li key={i} className="text-sm">
                        <div className="flex items-center gap-2 flex-wrap">
                          <Badge label={cap(f.kind.replace(/_/g, " "))} status="danger" size="sm" />
                          <span className="font-medium text-text">{f.subject || `User #${f.subjectUserId}`}</span>
                        </div>
                        <div className="mt-0.5 text-xs text-text-2">{f.detail}</div>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>

            <div className="lg:col-span-2 space-y-4">
              <TableCard
                title="Users"
                subtitle={isAccessLoading ? "Loading…" : `Showing ${users.length} user${users.length === 1 ? "" : "s"}`}
                isLoading={isAccessLoading}
                error={accessQ.error}
                errorMessage="Error loading effective access"
                onRetry={() => accessQ.refetch()}
              >
                {users.length === 0 ? (
                  <EmptyState
                    icon={icon({ name: "users", style: "light" })}
                    title="No users found"
                    message="This tenant currently has no users."
                  />
                ) : (
                  <FlexTable columns={userColumns} data={users} getRowId={(u) => u.userId} />
                )}
              </TableCard>

              <TableCard
                title="Service accounts"
                subtitle={isAccessLoading ? "Loading…" : `Showing ${serviceAccounts.length} service account${serviceAccounts.length === 1 ? "" : "s"}`}
                isLoading={isAccessLoading}
              >
                {serviceAccounts.length === 0 ? (
                  <EmptyState
                    icon={icon({ name: "robot", style: "light" })}
                    title="No service accounts"
                    message="This tenant currently has no service accounts."
                  />
                ) : (
                  <FlexTable columns={serviceAccountColumns} data={serviceAccounts} getRowId={(a) => a.id} />
                )}
              </TableCard>
            </div>
          </div>
        </section>
      </div>
    </Fragment>
  );
}
